import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChartStyles } from './chartstyles';
import chartData from '../../../static/data/data.json';
import {
  clearInteractiveAreaCode,
  setHoveredAreaCode,
  setPinnedAreaCode,
} from '../../state/slices/data';

const GROUP_ORDER = [
  'Atlantic Ocean, Mediterranean & Black Sea',
  'Indian Ocean',
  'Pacific Ocean',
  'Antarctic Areas',
];

const getGroupName = (areaName) => {
  if (
    areaName === 'Mediterranean and Black Sea' ||
    areaName.startsWith('Atlantic')
  ) {
    return 'Atlantic Ocean, Mediterranean & Black Sea';
  }

  if (areaName.startsWith('Indian Ocean')) {
    return 'Indian Ocean';
  }

  if (areaName.startsWith('Pacific')) {
    return 'Pacific Ocean';
  }

  return 'Antarctic Areas';
};

const formatPct = (value) => `${Number.parseFloat(value || 0).toFixed(1)}%`;
const getInteractiveCodes = (item) =>
  item.F_CODE === '48-58-88' ? ['48', '58', '88'] : [item.F_CODE];

const buildGroupItems = (groupName) => {
  const items = chartData.filter(
    (item) => getGroupName(item.AREA) === groupName,
  );

  if (groupName !== 'Antarctic Areas') {
    return items;
  }

  const totalItems = items.length || 1;
  const sustainableTotal = items.reduce(
    (sum, item) => sum + Number.parseFloat(item.SUSTAINABLE || 0),
    0,
  );
  const unsustainableTotal = items.reduce(
    (sum, item) => sum + Number.parseFloat(item.UNSUSTAINABLE || 0),
    0,
  );

  return [
    {
      AREA: 'Antarctic Areas',
      F_CODE: '48-58-88',
      SUSTAINABLE: `${sustainableTotal / totalItems}`,
      UNSUSTAINABLE: `${unsustainableTotal / totalItems}`,
    },
  ];
};

const groupedData = GROUP_ORDER.map((groupName) => ({
  title: groupName,
  items: buildGroupItems(groupName),
}));

export default function Charts() {
  const dispatch = useDispatch();
  const hoveredAreaCode = useSelector((state) => state.data.hoveredAreaCode);
  const pinnedAreaCode = useSelector((state) => state.data.pinnedAreaCode);
  const activeCodes = Array.isArray(pinnedAreaCode || hoveredAreaCode)
    ? pinnedAreaCode || hoveredAreaCode
    : pinnedAreaCode || hoveredAreaCode
      ? [pinnedAreaCode || hoveredAreaCode]
      : [];

  return (
    <ChartStyles>
      <div className="chart-columns chart-columns-global">
        <div />
        <div className="chart-column-heading chart-column-heading-right">
          <span className="chart-bar-headings">
            <span className="chart-bar-heading chart-bar-heading-unsustainable">
              Unsustainable
            </span>
            <span className="chart-bar-heading chart-bar-heading-sustainable">
              Sustainable
            </span>
          </span>
        </div>
      </div>

      {groupedData.map((group) => (
        <section key={group.title} className="chart-group">
          <h2 className="chart-group-title">{group.title}</h2>

          <div className="chart-rows">
            {group.items.map((item) => {
              const unsustainable = Number.parseFloat(item.UNSUSTAINABLE || 0);
              const sustainable = Number.parseFloat(item.SUSTAINABLE || 0);
              const interactiveCodes = getInteractiveCodes(item);
              const pinnedCodes = Array.isArray(pinnedAreaCode)
                ? pinnedAreaCode
                : pinnedAreaCode
                  ? [pinnedAreaCode]
                  : [];
              const isActive = interactiveCodes.some((code) =>
                activeCodes.includes(code),
              );
              const isPinned = interactiveCodes.every((code) =>
                pinnedCodes.includes(code),
              );
              const togglePinnedState = () => {
                if (isPinned) {
                  dispatch(clearInteractiveAreaCode());
                  return;
                }

                dispatch(
                  setPinnedAreaCode(
                    interactiveCodes.length === 1
                      ? interactiveCodes[0]
                      : interactiveCodes,
                  ),
                );
                dispatch(setHoveredAreaCode(null));
              };

              return (
                <div
                  key={`${group.title}-${item.F_CODE}`}
                  className={`chart-row ${isActive ? 'is-active' : ''}`}
                  role="button"
                  tabIndex={0}
                  onMouseEnter={() => {
                    if (pinnedCodes.length) return;
                    dispatch(
                      setHoveredAreaCode(
                        interactiveCodes.length === 1
                          ? interactiveCodes[0]
                          : interactiveCodes,
                      ),
                    );
                  }}
                  onMouseLeave={() => {
                    if (pinnedCodes.length) return;
                    dispatch(setHoveredAreaCode(null));
                  }}
                  onClick={togglePinnedState}
                  onTouchStart={togglePinnedState}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    togglePinnedState();
                  }}
                >
                  <div className="chart-area">
                    <span className="chart-area-name">{item.AREA}</span>
                  </div>

                  <div className="chart-bar-side">
                    <div className="chart-bar-line">
                      <span className="chart-bar-pct chart-bar-pct-unsustainable">
                        {formatPct(item.UNSUSTAINABLE)}
                      </span>

                      <div className="chart-bar-wrap" aria-hidden="true">
                        <div
                          className="chart-bar chart-bar-unsustainable"
                          style={{ width: `${unsustainable}%` }}
                        />
                        <div
                          className="chart-bar chart-bar-sustainable"
                          style={{ width: `${sustainable}%` }}
                        />
                      </div>

                      <span className="chart-bar-pct chart-bar-pct-sustainable">
                        {formatPct(item.SUSTAINABLE)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </ChartStyles>
  );
}
